import React, { useEffect, useState } from 'react'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import { FormGroup } from './Inputs/Input.css'
import NewButton from './NewButton'
import { ButtonText } from '../../stories/elements/Typography/Text'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import Icon from 'assets/icons/search.svg'
import useDebounce from 'lib/hooks/useDebounce'
import useMedia from 'lib/hooks/useMedia'

const SearchIcon = styled((props) => <Icon {...props} />)<{
    isSmall?: boolean
}>`
    position: absolute;
    left: 15px;
    top: ${({ isSmall }) => (isSmall ? 22.5 : 30)}px;
`

const Container = styled.div<{
    readonly fullWidth?: boolean
    readonly isSmall?: boolean
    readonly hideButton?: boolean
    readonly isDarkTheme?: boolean
}>`
    margin-top: 0px;
    display: grid;
    grid-template-columns: 3fr auto;
    grid-gap: 5px;
    align-items: center;
    padding-left: 5px;
    ${({ isSmall }) =>
        isSmall
            ? css`
                  && input {
                      height: 45px !important;
                      min-height: 45px !important;
                  }
              `
            : ''}
    .icon-button {
        text-indent: 25px;
        background: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.backgroundTransparent : '#fff')} !important;
        color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.grayScaleSecondary : 'inherit')};
        border: 1px solid ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : `${theme.colors.contentBody}33`)};

        ::placeholder {
            color: ${({ isDarkTheme }) =>
                isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.placeholderText} !important;
            opacity: 1;
            font-family: var(--font-inter);
            font-weight: 500 !important;
            font-size: 16px;
        }

        ${({ isDarkTheme }) =>
            isDarkTheme &&
            css`
                :focus {
                    border: 1px solid ${theme.colors.backgroundTransparent};
                }
            `}
    }
    @media (max-width: 750px) {
        ${({ hideButton }) =>
            !hideButton
                ? css`
                      grid-template-columns: 3fr 1fr;
                  `
                : ''}
    }
`

interface Props {
    onSearch(value: string): void
    buttonText: string
    placeholder?: string
    defaultValue?: string | null
    isSmall?: boolean
    onChange?: () => void
    onDebounce?: (query: string) => void
    hideButton?: boolean
    isDarkTheme?: boolean
}

const Search = (props: Props) => {
    const [value, setValue] = useState(props.defaultValue || '')
    const debouncedSearchValue = useDebounce(value, 500)
    const md = useMedia(deviceSize.md)

    useEffect(() => {
        if (!props.onDebounce) return

        if (debouncedSearchValue || debouncedSearchValue.length === 0) {
            props.onDebounce(debouncedSearchValue)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchValue, props.onDebounce])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange && props.onChange()
        setValue(e.target.value)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            props.onSearch(value)
        }
    }

    const handleSubmit = () => {
        props.onSearch(value)
    }

    return (
        <Container isSmall={props.isSmall} hideButton={props.hideButton} isDarkTheme={props.isDarkTheme}>
            <FormGroup noMargin height={props.isSmall ? 10 : 60} isRelative darkTheme={props.isDarkTheme}>
                <SearchIcon isSmall={props.isSmall} />
                <input
                    onChange={handleChange}
                    defaultValue={props.defaultValue || ''}
                    onKeyDown={handleKeyDown}
                    placeholder={props.placeholder ?? ''}
                    className="icon-button"
                />
            </FormGroup>
            {!props.hideButton ? (
                <NewButton
                    onClick={handleSubmit}
                    borderRadius={theme.borderRadius.normal}
                    width={md || props.isSmall ? 100 : 180}
                >
                    <ButtonText color={theme.colors.white}>{props.buttonText ?? ''}</ButtonText>
                </NewButton>
            ) : null}
        </Container>
    )
}

export default Search
